import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { BarChart3, Bot, FileText, Loader2, MessagesSquare } from "lucide-react";
import Layout from "@/components/Layout";
import { getApiBase } from "@/lib/httpClient";
import { LEITURA } from "../lib/larguras";

const PERIODOS = [
  { dias: 7, rotulo: "7 dias" },
  { dias: 30, rotulo: "30 dias" },
  { dias: 90, rotulo: "90 dias" },
];

const ORIGENS = {
  chat: { rotulo: "Conversas no assistente", icone: MessagesSquare },
  intimacao: { rotulo: "Leitura de intimações", icone: FileText },
};

export function formatarTokens(valor) {
  const n = Number(valor || 0);
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

// O custo é estimado a partir da tabela do provedor, quando ele publica uma.
// Onde não publica, o extrato mostra tokens e cala sobre dinheiro — número
// inventado aqui viraria divergência com a fatura.
export function formatarCusto(valor) {
  if (valor == null) return "—";
  return `US$ ${Number(valor).toFixed(valor < 0.01 ? 4 : 2)}`;
}

export function formatarQuando(iso) {
  if (!iso) return "—";
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return "—";
  return data.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function Numero({ rotulo, valor, detalhe }) {
  return (
    <div className="border border-[#D1DEEE] rounded-lg p-4 bg-white">
      <p className="text-[#335480] text-sm">{rotulo}</p>
      <p className="text-2xl font-bold text-[#001533] tabular-nums mt-0.5" style={{ fontFamily: "Cabinet Grotesk" }}>
        {valor}
      </p>
      {detalhe && <p className="text-[#6685AD] text-xs mt-0.5">{detalhe}</p>}
    </div>
  );
}

export default function UsoIAPage() {
  const [dias, setDias] = useState(30);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);

  const carregar = useCallback(async (periodo) => {
    setCarregando(true);
    try {
      const { data } = await axios.get(`${getApiBase()}/chat/uso`, {
        params: { dias: periodo },
        withCredentials: true,
      });
      setDados(data);
    } catch {
      setDados(null);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(dias); }, [dias, carregar]);

  const total = dados?.total;
  const semUso = !carregando && total && total.chamadas === 0;

  return (
    <Layout>
      <div className={`${LEITURA} space-y-6`}>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-[#335480] text-sm uppercase tracking-widest font-medium mb-1">Assistente</p>
            <h1 className="text-4xl font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>
              Extrato de uso
            </h1>
            <p className="text-[#335480] mt-1.5">
              O que o assistente consumiu na sua conta do provedor — conversas e leitura de intimações.
            </p>
          </div>
          <div className="flex gap-1 border border-[#D1DEEE] rounded-lg p-1 bg-white" role="tablist">
            {PERIODOS.map((p) => (
              <button
                key={p.dias}
                type="button"
                role="tab"
                aria-selected={p.dias === dias}
                data-testid={`periodo-${p.dias}`}
                onClick={() => setDias(p.dias)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium ${
                  p.dias === dias ? "bg-[#003882] text-white" : "text-[#335480] hover:text-[#003882]"
                }`}
              >
                {p.rotulo}
              </button>
            ))}
          </div>
        </div>

        {carregando && (
          <p className="text-[#335480] flex items-center gap-2" data-testid="uso-carregando">
            <Loader2 size={16} className="animate-spin" aria-hidden="true" /> Carregando extrato...
          </p>
        )}

        {!carregando && !dados && (
          <p className="text-[#335480]" data-testid="uso-erro">Não foi possível carregar o extrato agora.</p>
        )}

        {!carregando && semUso && (
          <div className="border border-[#D1DEEE] bg-white rounded-lg p-8 text-center" data-testid="uso-vazio">
            <Bot size={32} strokeWidth={1.5} className="text-[#6685AD] mx-auto mb-3" aria-hidden="true" />
            <p className="text-[#001533] font-medium">Nenhum consumo no período</p>
            <p className="text-[#335480] mt-1.5">
              O extrato enche quando você conversa com o <Link to="/assistente" className="text-[#003882] underline">assistente</Link>{" "}
              ou liga a leitura automática das intimações.
            </p>
          </div>
        )}

        {!carregando && dados && !semUso && (
          <>
            <div className="grid gap-3 sm:grid-cols-4" data-testid="uso-totais">
              <Numero rotulo="Chamadas ao modelo" valor={total.chamadas} />
              <Numero rotulo="Tokens de entrada" valor={formatarTokens(total.entrada)} />
              <Numero rotulo="Tokens de saída" valor={formatarTokens(total.saida)} />
              <Numero
                rotulo="Custo estimado"
                valor={formatarCusto(total.custo_usd)}
                detalhe={total.custo_parcial ? "parcial — há modelos sem preço publicado" : "estimativa; a fatura é do provedor"}
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {dados.por_origem.map((linha) => {
                const { rotulo, icone: Icone } = ORIGENS[linha.origem] || { rotulo: linha.origem, icone: Bot };
                return (
                  <div key={linha.origem} className="border border-[#D1DEEE] rounded-lg p-4 bg-white flex items-start gap-3" data-testid="uso-origem">
                    <Icone size={20} strokeWidth={1.5} className="text-[#003882] mt-0.5" aria-hidden="true" />
                    <div>
                      <p className="text-[#001533] font-medium">{rotulo}</p>
                      <p className="text-[#335480] text-sm mt-0.5 tabular-nums">
                        {linha.chamadas} chamadas · {formatarTokens(linha.entrada + linha.saida)} tokens ·{" "}
                        {formatarCusto(linha.custo_parcial ? null : linha.custo_usd)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="border border-[#D1DEEE] rounded-lg bg-white overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-[#D1DEEE]">
                <BarChart3 size={18} strokeWidth={1.5} className="text-[#003882]" aria-hidden="true" />
                <h2 className="font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>Por modelo</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F4F7FB] text-[#335480] text-left">
                    <tr>
                      <th scope="col" className="font-medium px-4 py-2">Modelo</th>
                      <th scope="col" className="font-medium px-4 py-2">Chamadas</th>
                      <th scope="col" className="font-medium px-4 py-2">Entrada</th>
                      <th scope="col" className="font-medium px-4 py-2">Saída</th>
                      <th scope="col" className="font-medium px-4 py-2">Custo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dados.por_modelo.map((linha) => (
                      <tr key={linha.modelo} className="border-t border-[#D1DEEE]" data-testid="uso-modelo">
                        <td className="px-4 py-2 text-[#001533]">{linha.modelo}</td>
                        <td className="px-4 py-2 text-[#335480] tabular-nums">{linha.chamadas}</td>
                        <td className="px-4 py-2 text-[#335480] tabular-nums">{formatarTokens(linha.entrada)}</td>
                        <td className="px-4 py-2 text-[#335480] tabular-nums">{formatarTokens(linha.saida)}</td>
                        <td className="px-4 py-2 text-[#335480] tabular-nums">
                          {formatarCusto(linha.custo_parcial ? null : linha.custo_usd)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="border border-[#D1DEEE] rounded-lg bg-white overflow-hidden">
              <div className="px-4 py-3 border-b border-[#D1DEEE]">
                <h2 className="font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>Lançamentos</h2>
              </div>
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F4F7FB] text-[#335480] text-left sticky top-0">
                    <tr>
                      <th scope="col" className="font-medium px-4 py-2">Quando</th>
                      <th scope="col" className="font-medium px-4 py-2">Origem</th>
                      <th scope="col" className="font-medium px-4 py-2">Modelo</th>
                      <th scope="col" className="font-medium px-4 py-2">Tokens</th>
                      <th scope="col" className="font-medium px-4 py-2">Custo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dados.lancamentos.map((linha, i) => (
                      <tr key={i} className="border-t border-[#D1DEEE]" data-testid="uso-lancamento">
                        <td className="px-4 py-2 text-[#335480] whitespace-nowrap">{formatarQuando(linha.quando)}</td>
                        <td className="px-4 py-2 text-[#335480]">
                          {(ORIGENS[linha.origem] || {}).rotulo || linha.origem}
                          {linha.resultado === "erro" && (
                            <span className="ml-2 text-xs rounded-full bg-[#F4F7FB] border border-[#D1DEEE] px-2 py-0.5">
                              falhou
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-[#001533]">{linha.modelo}</td>
                        <td className="px-4 py-2 text-[#335480] tabular-nums whitespace-nowrap">
                          {formatarTokens(linha.entrada)} / {formatarTokens(linha.saida)}
                        </td>
                        <td className="px-4 py-2 text-[#335480] tabular-nums">{formatarCusto(linha.custo_usd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <p className="text-[#6685AD] text-sm">
              O TUTO não cobra por mensagem. O valor real é o da fatura do seu provedor; aqui é a
              estimativa a partir dos tokens contados em cada chamada.
            </p>
          </>
        )}
      </div>
    </Layout>
  );
}
